/**
 * Pure mention display/normalize helpers.
 *
 * Zero runtime imports (local structural types only) so this file stays
 * pure. Tested by test/mentions-transform.ts.
 */

export type MentionRole = {
    id: string;
    name?: string | null;
    colour?: string | null;
};

export type MentionUser = {
    _id: string;
    username: string;
    display_name?: string | null;
    nickname?: string | null;
};

export type MentionClient = {
    users: { get(id: string): MentionUser | undefined };
    members: Map<string, unknown>;
};

/* Source of truth: RE_MENTIONS / RE_MENTION_ROLES in
 * packages/revolt.js/src/Client.ts. Duplicated locally so this file
 * stays node-importable with zero dependencies. */
const RE_ID_TAGS = /<([@%])([A-z0-9]{26})>/g;
const RE_NAME_TAGS = /<@(.+?)>/g;

/* Users of the given server, resolved from client.members keys
 * (JSON.stringify({ server, user })), exactly like the MessageBox
 * closures used to. For non-server channels (DMs, groups) falls back
 * to all users available in client.users. */
function serverUsers(
    client: MentionClient,
    server_id?: string | null,
): MentionUser[] {
    if (server_id) {
        return [...client.members.keys()]
            .map((x) => JSON.parse(x))
            .filter((x) => x.server === server_id)
            .map((x) => client.users.get(x.user))
            .filter((x): x is MentionUser => x !== undefined);
    }

    /* Non-server channel — return every user we know about. */
    return [...client.users.values()] as MentionUser[];
}

/**
 * Turn mention tags into display names: `<@userId>`/`<@roleId>`/`<%roleId>`
 * become `<@Name>`; unknown tags pass through untouched.
 * Called by MessageBox (draft display), MessageEditor (textarea value)
 * and ForwardPicker (forward preview).
 */
export function displayMentions(
    s: string,
    client: MentionClient,
    server_id?: string | null,
    roles: MentionRole[] = [],
): string {
    const users = serverUsers(client, server_id);

    return s.replaceAll(RE_ID_TAGS, (match, tag: string, id: string) => {
        if (tag === "%") {
            const role = roles.find((r) => r.id === id);
            return role?.name ? `<@${role.name}>` : match;
        }

        // tag === "@"
        const role = roles.find((r) => r.id === id);
        if (role?.name) return `<@${role.name}>`;

        const user = users.find((u) => u._id === id);
        const name = user?.display_name || user?.username || id;
        return user ? `<@${name}>` : match;
    });
}

/**
 * Turn display mention tags back into canonical ids: `<@everyone>` stays,
 * role by name/id becomes `<%roleId>`, user by id/display_name/username/
 * nickname becomes `<@userId>`; `<%roleId>` passes through untouched.
 * Called by MessageBox (send) and MessageEditor (save).
 */
export function normalizeMentions(
    s: string,
    client: MentionClient,
    server_id?: string | null,
    roles: MentionRole[] = [],
): string {
    const users = serverUsers(client, server_id);

    return s.replaceAll(RE_NAME_TAGS, (match: string, name: string) => {
        if (name === "everyone") return "<@everyone>";

        const role = roles.find((r) => r.id === name || r.name === name);
        if (role) return `<%${role.id}>`;

        const user = users.find(
            (u) =>
                u._id === name ||
                u.display_name === name ||
                u.username === name ||
                u.nickname === name,
        );
        if (user) return `<@${user._id}>`;

        return match;
    });
}
